import React, { useState, useMemo, useCallback, memo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { FareCard } from './FareCard';
import { PageIndicators } from './PageIndicators';
import { Legend } from './Legend';
export const CARD_WIDTH = 320;
const CARD_GAP = 16;

const FareDialog = ({ open, onClose, data = [], currency }) => {
  const theme = useTheme();
  const colorTheme = useColors();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentPage, setCurrentPage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(1);
  const containerRef = useRef();

  const calculateCardsPerPage = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const computedStyle = window.getComputedStyle(containerRef.current);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      const effectiveWidth = containerWidth - paddingLeft - paddingRight;
      const calculatedCards = Math.floor((effectiveWidth + CARD_GAP) / (CARD_WIDTH + CARD_GAP));
      // setCardsPerPage(calculatedCards > 0 ? calculatedCards : 1);
    }
  }, []);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => calculateCardsPerPage());
    }
    const handleResize = () => calculateCardsPerPage();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [open, calculateCardsPerPage]);

  const { totalPages, currentFares, isLastPage, showArrows } = useMemo(() => {
    const total = Math.ceil(data.length / cardsPerPage);
    const startIndex =
      currentPage === 0 ? currentPage * cardsPerPage : currentPage * cardsPerPage - 1;
    const currentFares = data.slice(
      startIndex,
      currentPage > 0 ? startIndex + cardsPerPage + 2 : startIndex + cardsPerPage + 1
    );

    return {
      totalPages: total,
      currentFares,
      isLastPage: currentPage === total - 1,
      showArrows: total > 1,
    };
  }, [data, cardsPerPage, currentPage]);

  const baseFare = useMemo(() => data[0], [data]);

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : prev));
  }, [totalPages]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  return (
    <Dialog
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: '100%', sm: '42rem' },
          m: { xs: 0, sm: 2 },
          borderRadius: '15px',
        },
      }}
      fullScreen={isMobile}
      maxWidth={false}
      onClose={onClose}
      open={open}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box component='div'>
          <Typography variant='h6'>Opciones de Tarifas</Typography>
        </Box>
        <IconButton aria-label='close' color='inherit' edge='end' onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
        }}
      >
        {showArrows && (
          <>
            <IconButton
              disabled={!currentPage}
              onClick={handlePrevPage}
              sx={{
                display: { sm: 'flex', xs: 'none' },
                position: 'absolute',
                left: { xs: '0.5rem', sm: '2rem' },
                top: 'calc(50% - 2rem)',
                transform: 'translateY(-50%)',
              }}
            >
              <ChevronLeft />
            </IconButton>

            <IconButton
              disabled={isLastPage}
              onClick={handleNextPage}
              sx={{
                display: { sm: 'flex', xs: 'none' },
                position: 'absolute',
                right: { xs: '0.5rem', sm: '2rem' },
                top: 'calc(50% - 2rem)',
                transform: 'translateY(-50%)',
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}
        <Box
          sx={{
            py: 2,
            justifyContent: 'center',
            px: { xs: 3, sm: 6 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            margin: 'auto',
            overflow: { xs: 'unset', sm: 'hidden' },
            maxWidth: { xs: '21rem', sm: '28rem' },
            marginLeft: { xs: '-1,5rem', sm: 'auto' },
            flex: 1,
          }}
        >
          <Box
            ref={containerRef}
            sx={{
              display: 'flex',
              gap: `${CARD_GAP}px`,
              justifyItems: 'center',
              overflow: 'hidden',
            }}
          >
            {currentPage === 0 && <Box style={{ width: `${CARD_WIDTH}px` }} />}
            {currentFares.map((fare, index) => (
              <FareCard
                baseFare={baseFare}
                currency={currency}
                disabled={currentPage > 0 ? index !== 1 : !!index}
                fare={fare}
                key={fare.id}
                {...colorTheme}
              />
            ))}
            {currentPage === totalPages - 1 && <Box style={{ width: `${CARD_WIDTH}px` }} />}
          </Box>

          {showArrows && (
            <PageIndicators
              currentPage={currentPage}
              handleNextPage={handleNextPage}
              handlePrevPage={handlePrevPage}
              onPageChange={handlePageChange}
              totalPages={totalPages}
              {...colorTheme}
            />
          )}
        </Box>
        <Legend />
      </DialogContent>
    </Dialog>
  );
};

export default memo(FareDialog);
